import { Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useSwipeable } from "react-swipeable";
import PreviewThumbnails from "./PreviewThumbnails";
import PinchZoom from "./PinchZoom";
import { parsePageList } from "../../../utils/pageRange";
import { ensurePdfWorker } from "../pdfWorkerSetup";
import { usePdfPageFit } from "./usePdfPageFit";
import { landscapeScale } from "./pdfFit";
import { fetchPdfBlob } from "./pdfBlobCache";

// Запускаем загрузку воркера сразу при импорте модуля (не дожидаясь первого
// монтирования компонента) — к моменту реального обращения он с большей
// вероятностью уже готов.
ensurePdfWorker();

const MAX_ZOOM = 4;
// Отрисовываем в повышенном разрешении, чтобы при увеличении щипком
// страница оставалась чёткой, а не мыльной.
const RENDER_DPR = (window.devicePixelRatio || 1) * 2;

function PDFViewer({
    file,
    printType,
    customPages,
    onLoadSuccess,
    printColor,
    orientation = "portrait",
}) {
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfData, setPdfData] = useState(null); // Blob URL (строка)
    const [pdfProxy, setPdfProxy] = useState(null); // PDFDocumentProxy — для реального соотношения сторон
    // Код ошибки + параметры для t(), НЕ готовая строка: строка перевода не
    // должна жить в зависимостях эффекта загрузки (см. errorInfo ниже и
    // комментарий у useEffect) — иначе смена языка перезапускает загрузку PDF.
    const [errorInfo, setErrorInfo] = useState(null); // {code, params?} | null
    const [retryCount, setRetryCount] = useState(0);
    const [forceReload, setForceReload] = useState(0);

    const accessToken = localStorage.getItem("access_token");
    const { t } = useTranslation();

    useEffect(() => {
        let isMounted = true;

        // Сразу сбрасываем старый файл → включится загрузчик (иначе при
        // переключении между документами какое-то время виден предыдущий PDF).
        setPdfData(null);
        setPdfProxy(null);

        const loadPDF = async () => {
            // Ждём, пока воркер PDF.js будет готов (см. pdfWorkerSetup.js) —
            // <Document>/<Page> ниже полагаются на уже настроенный
            // GlobalWorkerOptions.workerSrc при первом обращении к pdf.js.
            await ensurePdfWorker();
            if (!isMounted) return;

            if (!file) {
                if (isMounted) setErrorInfo({ code: "error_no_file" });
                return;
            }

            // Проверяем, что file — это строка и она корректна
            if (typeof file !== "string" || !file.startsWith("http")) {
                if (typeof file === "object" && file?.data) {
                    // Если file — объект с данными (буфер)
                    const blob = new Blob([file.data], { type: "application/pdf" });
                    const blobUrl = URL.createObjectURL(blob);
                    if (isMounted) {
                        setPdfData(blobUrl);
                        setErrorInfo(null);
                    }
                    return;
                }
                if (isMounted) setErrorInfo({ code: "error_invalid_file" });
                return;
            }

            try {
                // Общий кэш с DocView.jsx и фоновой предзагрузкой
                // (PreloadDocumentPages.jsx, см. pdfBlobCache.js) — если
                // документ уже предзагружен в фоне (EditManyDocs.jsx),
                // здесь сеть не понадобится вовсе.
                const blobUrl = await fetchPdfBlob(file, accessToken);
                if (isMounted) {
                    setPdfData(blobUrl);
                    setErrorInfo(null);
                }
            } catch (err) {
                if (isMounted) {
                    setRetryCount(prev => prev + 1);
                    setErrorInfo({
                        code: "error_load_failed",
                        params: { message: err.message },
                    });
                    setPdfData(null);
                    console.error("PDFViewer: Load error", file, err);
                }
            }
        };

        if (accessToken) {
            loadPDF();
        } else {
            if (isMounted) setErrorInfo({ code: "error_no_token" });
        }

        return () => {
            isMounted = false;
        };
        // `t` НЕ в зависимостях намеренно: react-i18next отдаёт новый `t` на
        // каждый languageChanged, а этот эффект перекачивает файл заново
        // (setPdfData(null) выше). Раньше `t` был в deps только чтобы
        // подставить текст ошибки — теперь текст переводится при рендере
        // (errorText ниже), а сюда попадает только код ошибки.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, accessToken, forceReload]);

    const memoizedPdfData = useMemo(() => pdfData, [pdfData]);

    const getPagesToShow = useMemo(() => {
        if (!numPages) return [];
        if (printType === "first_page") return [1];
        if (printType === "last_page") return [numPages];
        if (printType === "custom" && customPages) {
            return parsePageList(customPages, numPages);
        }
        return Array.from({ length: numPages }, (_, i) => i + 1);
    }, [printType, customPages, numPages]);

    const handleDocumentLoadSuccess = (pdf) => {
        setNumPages(pdf.numPages);
        setPdfProxy(pdf);
        onLoadSuccess(getPagesToShow.length || pdf.numPages);
    };

    const handlers = useSwipeable({
        onSwipedLeft: () => {
            const nextPage = getPagesToShow.find((page) => page > pageNumber);
            if (nextPage) setPageNumber(nextPage);
        },
        onSwipedRight: () => {
            const prevPage = getPagesToShow
                .slice()
                .reverse()
                .find((page) => page < pageNumber);
            if (prevPage) setPageNumber(prevPage);
        },
        delta: 10,
        preventScrollOnSwipe: true,
    });

    useEffect(() => {
        if (getPagesToShow.length > 0 && !getPagesToShow.includes(pageNumber)) {
            setPageNumber(getPagesToShow[0] || 1);
        }
    }, [getPagesToShow, pageNumber]);

    const isGrayscale = printColor === "black_white";
    const isLandscape = orientation === "landscape";
    // Реальная область под страницу: её высота меняется, когда под превью
    // появляются плашки-предупреждения (см. usePdfPageFit). Через state, а не
    // useRef: блок монтируется только после загрузки PDF, и эффект измерения
    // должен перезапуститься именно в этот момент.
    const [pageBoxEl, setPageBoxEl] = useState(null);
    // Вписываем страницу в реально доступную область по её РЕАЛЬНОМУ
    // соотношению сторон (чек, длинная квитанция, альбомный скан и т.п. —
    // не только A4), см. usePdfPageFit.js.
    const { box, pageWidth, pageHeight, ready: pageFitReady } =
        usePdfPageFit(pdfProxy, pageNumber, pageBoxEl);
    // Альбомная ориентация: разворачиваем лист. Масштаб считаем от реального
    // соотношения сторон страницы и области, а не захардкоженным 0.7071
    // (верным только для A4) — иначе повёрнутый чек вылезал бы за карточку.
    const rotateScale = isLandscape
        ? landscapeScale(pageWidth, pageHeight, box.width, box.height)
        : 1;

    const canGoPrev = getPagesToShow.some((page) => page < pageNumber);
    const canGoNext = getPagesToShow.some((page) => page > pageNumber);

    if (errorInfo) {
        return (
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-[1.4vh] py-[2vh]">
                {/* Ошибка или предупреждение */}
                <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-center text-[1.4vh] max-w-[26vw] font-semibold px-[1.4vw] py-[1vh] rounded-[1.6vh] bg-[#F7E1DC] text-[#C2543E]"
                >
                    {retryCount >= 3
                        ? t("fileView.error_broken")
                        : t(`fileView.${errorInfo.code}`, errorInfo.params)}
                </motion.div>

                {/* Кнопка обновить */}
                <motion.button data-analytics="pdfview.fileView.retry.1"
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                        setErrorInfo(null);
                        setPdfData(null);
                        setForceReload((prev) => prev + 1);
                    }}
                    className="px-[2vw] py-[1.1vh] rounded-full bg-[#246CE2] text-white text-[1.5vh] font-bold active:bg-[#1B55B4] transition-colors"
                >
                    {t("fileView.retry")}
                </motion.button>

                {retryCount > 0 && retryCount < 3 && (
                    <div className="text-[#7B8290] text-[1.2vh] font-semibold">
                        {t("fileView.attempt", { current: retryCount, total: 3 })}
                    </div>
                )}
            </div>
        );
    }


    if (!memoizedPdfData) {
        return (
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-[1.2vh]">
                <svg
                    className="w-[3.6vh] h-[3.6vh]"
                    style={{ animation: "spin 0.8s linear infinite" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#246CE2"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                >
                    <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                </svg>
                <div className="text-[1.6vh] font-semibold text-[#7B8290]">
                    {t("fileView.loading")}
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex-1 min-h-0 w-full flex flex-col items-center gap-[1vh]"
            {...handlers}
        >
            {/* overflow-hidden — подстраховка на случай пограничной погрешности
                округления в вычислении ширины: даже если страница окажется
                на пару пикселей больше area, она обрежется здесь, а не
                вылезет поверх остального интерфейса. */}
            <div
                ref={setPageBoxEl}
                className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden"
            >
                <Document
                    file={memoizedPdfData}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    onLoadError={(err) => {
                        setErrorInfo({
                            code: "error_render_failed",
                            params: { message: err.message },
                        });
                        console.error("PDFViewer: Render error", err);
                    }}
                >
                    {pageFitReady ? (
                        <PinchZoom key={pageNumber} maxScale={MAX_ZOOM}>
                            <div
                                className={`${isGrayscale ? "grayscale" : ""} border-[0.25vh] border-[#E1E4EB] rounded-[1.2vh] overflow-hidden bg-white`}
                                style={
                                    isLandscape
                                        ? { transform: `rotate(90deg) scale(${rotateScale})` }
                                        : undefined
                                }
                            >
                                <Page
                                    pageNumber={pageNumber}
                                    width={pageWidth}
                                    devicePixelRatio={RENDER_DPR}
                                />
                            </div>
                        </PinchZoom>
                    ) : (
                        // Соотношение сторон страницы (или размер области) ещё не
                        // известны — не рендерим <Page> с заведомо неверной шириной
                        // (иначе для нестандартного документа была бы короткая
                        // вспышка неверного размера перед перерисовкой).
                        <svg
                            className="w-[3.6vh] h-[3.6vh]"
                            style={{ animation: "spin 0.8s linear infinite" }}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#246CE2"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                        >
                            <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                        </svg>
                    )}
                </Document>
            </div>

            {numPages && (
                <div className="flex-none flex items-center gap-[1.4vw]">
                    <button data-analytics="pdfview.action.2"
                        type="button"
                        className="w-[4.4vh] h-[4.4vh] rounded-full bg-[#F1F3F7] flex items-center justify-center disabled:opacity-30 active:bg-[#E1E4EB] transition-colors"
                        onClick={() => {
                            const prevPage = getPagesToShow
                                .slice()
                                .reverse()
                                .find((page) => page < pageNumber);
                            if (prevPage) setPageNumber(prevPage);
                        }}
                        disabled={!canGoPrev}
                        title={t("fileView.previous_page")}
                    >
                        <svg
                            className="w-[2vh] h-[2vh]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#12161F"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M15 6l-6 6 6 6" />
                        </svg>
                    </button>
                    <div className="text-[1.5vh] font-bold text-[#7B8290]">
                        {t("fileView.preview", {
                            current: getPagesToShow.indexOf(pageNumber) + 1,
                            total: getPagesToShow.length,
                        })}
                    </div>
                    <button data-analytics="pdfview.action.3"
                        type="button"
                        className="w-[4.4vh] h-[4.4vh] rounded-full bg-[#F1F3F7] flex items-center justify-center disabled:opacity-30 active:bg-[#E1E4EB] transition-colors"
                        onClick={() => {
                            const nextPage = getPagesToShow.find(
                                (page) => page > pageNumber
                            );
                            if (nextPage) setPageNumber(nextPage);
                        }}
                        disabled={!canGoNext}
                        title={t("fileView.next_page")}
                    >
                        <svg
                            className="w-[2vh] h-[2vh]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#12161F"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M9 6l6 6-6 6" />
                        </svg>
                    </button>
                </div>
            )}

            {numPages && (
                <div className="flex-none w-full">
                    <span className="block text-center text-[1.1vh] font-bold uppercase tracking-[0.15em] text-[#7B8290] mb-[0.6vh]">
                        {t("fileView.thumbnails")}
                    </span>
                    <PreviewThumbnails
                        file={memoizedPdfData}
                        pdfProxy={pdfProxy}
                        pages={getPagesToShow}
                        currentPage={pageNumber}
                        printColor={printColor}
                        onSelect={(p) => setPageNumber(p)}
                    />
                </div>
            )}
        </div>
    );
}

export default PDFViewer;
